// Shared form-fields surface for insurance Create/Edit/Renew. Consumed by
// InsuranceFormModal (write) and InsuranceDetailModal (read-only). All
// inputs are fully controlled; parent owns state + validation.
//
// Top-level Type dropdown (Insurance/Licence/Accreditation) drives
// which sub-form renders. `name` is derived server-side — never a form
// field. Waiver workflow removed entirely.

import { type ChangeEvent } from "react";
import CurrencyInput from "../../CurrencyInput";
import EvidenceLinksField from "../shared/EvidenceLinksField";
import { DEFAULT_EVIDENCE_LABEL } from "../../../../utils/evidenceLinks";
import type { InsuranceCategory, InsuranceType } from "../../../../types";
import {
  ALL_REMINDER_DAYS,
  INSURANCE_CATEGORY_LABELS,
  INSURANCE_TYPE_LABELS,
} from "../../../../types";
import { shortCoverageNote } from "./insuranceFormState";
import type {
  InsuranceFieldErrors,
  InsuranceFormState,
} from "./insuranceFormState";

export interface InsuranceFormFieldsProps {
  value: InsuranceFormState;
  onChange: (patch: Partial<InsuranceFormState>) => void;
  errors?: InsuranceFieldErrors;
  disabled?: boolean;
  idPrefix: string;
  /** Optional providers seen elsewhere on the contract; populates a
   *  <datalist> typeahead on the provider input. Free-text remains valid
   *  — the datalist is a suggestion, not a constraint. */
  providerSuggestions?: string[];
}

const CATEGORIES: InsuranceCategory[] = ["insurance", "licence", "accreditation"];

const INSURANCE_TYPE_OPTIONS: InsuranceType[] = [
  "public-liability",
  "professional-indemnity",
  "workers-compensation",
  "product-liability",
  "cyber-liability",
  "motor-vehicle",
  "other",
];

const REMINDER_OPTIONS: readonly number[] = ALL_REMINDER_DAYS;

export default function InsuranceFormFields({
  value,
  onChange,
  errors = {},
  disabled,
  idPrefix,
  providerSuggestions,
}: InsuranceFormFieldsProps) {
  const uniqueProviderSuggestions = providerSuggestions
    ? Array.from(new Set(providerSuggestions.filter(Boolean)))
    : [];
  const providerListId = `${idPrefix}-provider-list`;
  // Advisory (non-blocking) note when the coverage period looks suspiciously
  // short — suppressed while a real date error is showing so the two never
  // compete (the helper also returns null for invalid / expiry-before-start
  // dates, so this guard is belt-and-braces for intent).
  const coverageNote =
    errors.startDate || errors.expiryDate
      ? null
      : shortCoverageNote(value.startDate, value.expiryDate);
  const patch =
    <K extends keyof InsuranceFormState>(field: K) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onChange({ [field]: e.target.value } as Pick<InsuranceFormState, K>);

  const toggleReminder = (day: number) => {
    const next = value.reminderDays.includes(day)
      ? value.reminderDays.filter((d) => d !== day)
      : [...value.reminderDays, day].sort((a, b) => b - a);
    onChange({ reminderDays: next });
  };

  const toggleInsuranceType = (t: InsuranceType) => {
    const including = !value.insuranceTypes.includes(t);
    const next = including
      ? [...value.insuranceTypes, t]
      : value.insuranceTypes.filter((x) => x !== t);
    // Clear the conditional follow-up text when its trigger is unchecked
    // so a stale value doesn't survive in the patch.
    const patchExtras: Partial<InsuranceFormState> = {};
    if (!including && t === "other") patchExtras.otherInsuranceName = "";
    if (!including && t === "workers-compensation") {
      patchExtras.workersCompensationState = "";
    }
    onChange({ insuranceTypes: next, ...patchExtras });
  };

  const includesOther = value.insuranceTypes.includes("other");
  const includesWorkersComp = value.insuranceTypes.includes(
    "workers-compensation",
  );

  const isInsurance = value.category === "insurance";
  const subjectLabel =
    value.category === "licence"
      ? "Licence name"
      : value.category === "accreditation"
        ? "Accreditation name"
        : "Name";

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-category`} className="label">
          Type <span className="text-red-600">*</span>
        </label>
        <select
          id={`${idPrefix}-category`}
          className="input"
          value={value.category}
          onChange={(e) => {
            const nextCategory = e.target.value as InsuranceCategory;
            // Clear the other branch's fields so stale data doesn't leak.
            // minimumLimit is insurance-only so it must reset on switch off.
            onChange({
              category: nextCategory,
              ...(nextCategory === "insurance"
                ? { subjectName: "" }
                : { insuranceTypes: [], minimumLimit: 0 }),
            });
          }}
          disabled={disabled}
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {INSURANCE_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </div>

      {isInsurance ? (
        <fieldset className="space-y-2">
          <legend className="label">
            Insurance types <span className="text-red-600">*</span>
          </legend>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Select one or more. The stored name will combine the labels.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {INSURANCE_TYPE_OPTIONS.map((t) => (
              <label
                key={t}
                className="inline-flex items-center gap-2 text-body text-slate-700 dark:text-slate-300"
              >
                <input
                  type="checkbox"
                  className="accent-brand-600"
                  checked={value.insuranceTypes.includes(t)}
                  onChange={() => toggleInsuranceType(t)}
                  disabled={disabled}
                  aria-invalid={
                    !!errors.insuranceTypes && value.insuranceTypes.length === 0
                  }
                />
                {INSURANCE_TYPE_LABELS[t]}
              </label>
            ))}
          </div>
          {errors.insuranceTypes && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.insuranceTypes}
            </p>
          )}

          {includesOther && (
            <div>
              <label
                htmlFor={`${idPrefix}-other-name`}
                className="label"
              >
                Other insurance name <span className="text-red-600">*</span>
              </label>
              <input
                id={`${idPrefix}-other-name`}
                className="input"
                value={value.otherInsuranceName}
                onChange={patch("otherInsuranceName")}
                disabled={disabled}
                maxLength={200}
                placeholder="e.g. Marine Cargo"
                aria-invalid={!!errors.otherInsuranceName}
                aria-describedby={
                  errors.otherInsuranceName
                    ? `${idPrefix}-other-name-err`
                    : undefined
                }
              />
              {errors.otherInsuranceName && (
                <p
                  id={`${idPrefix}-other-name-err`}
                  className="mt-1 text-caption text-red-600 dark:text-red-400"
                >
                  {errors.otherInsuranceName}
                </p>
              )}
            </div>
          )}

          {includesWorkersComp && (
            <div>
              <label
                htmlFor={`${idPrefix}-wc-state`}
                className="label"
              >
                Workers compensation state{" "}
                <span className="text-red-600">*</span>
              </label>
              <input
                id={`${idPrefix}-wc-state`}
                className="input"
                value={value.workersCompensationState}
                onChange={patch("workersCompensationState")}
                disabled={disabled}
                maxLength={50}
                placeholder="e.g. QLD"
                aria-invalid={!!errors.workersCompensationState}
                aria-describedby={
                  errors.workersCompensationState
                    ? `${idPrefix}-wc-state-err`
                    : undefined
                }
              />
              {errors.workersCompensationState && (
                <p
                  id={`${idPrefix}-wc-state-err`}
                  className="mt-1 text-caption text-red-600 dark:text-red-400"
                >
                  {errors.workersCompensationState}
                </p>
              )}
            </div>
          )}
        </fieldset>
      ) : (
        <div>
          <label htmlFor={`${idPrefix}-subject`} className="label">
            {subjectLabel} <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-subject`}
            className="input"
            value={value.subjectName}
            onChange={patch("subjectName")}
            disabled={disabled}
            maxLength={200}
            aria-invalid={!!errors.subjectName}
            aria-describedby={
              errors.subjectName ? `${idPrefix}-subject-err` : undefined
            }
          />
          {errors.subjectName && (
            <p
              id={`${idPrefix}-subject-err`}
              className="mt-1 text-caption text-red-600 dark:text-red-400"
            >
              {errors.subjectName}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-provider`} className="label">
            Provider <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-provider`}
            className="input"
            value={value.provider}
            onChange={patch("provider")}
            disabled={disabled}
            maxLength={200}
            aria-invalid={!!errors.provider}
            list={providerListId}
            autoComplete="off"
          />
          <datalist id={providerListId}>
            {uniqueProviderSuggestions.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
          {errors.provider && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.provider}
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-policy`} className="label">
            {isInsurance ? "Policy number" : "Reference number"}
          </label>
          <input
            id={`${idPrefix}-policy`}
            className="input"
            value={value.policyNumber}
            onChange={patch("policyNumber")}
            disabled={disabled}
            maxLength={100}
          />
        </div>
      </div>

      {isInsurance && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor={`${idPrefix}-value`} className="label">
              Coverage value (AUD)
            </label>
            <CurrencyInput
              id={`${idPrefix}-value`}
              value={value.coverageValue}
              onChange={(v) => onChange({ coverageValue: v })}
              disabled={disabled}
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-min-limit`} className="label">
              Minimum limit
            </label>
            <CurrencyInput
              id={`${idPrefix}-min-limit`}
              value={value.minimumLimit}
              onChange={(v) => onChange({ minimumLimit: v })}
              disabled={disabled}
            />
            <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
              Leave blank for insurances with no dollar minimum (e.g. workers
              compensation).
            </p>
            {errors.minimumLimit && (
              <p
                id={`${idPrefix}-min-limit-err`}
                className="mt-1 text-caption text-red-600 dark:text-red-400"
              >
                {errors.minimumLimit}
              </p>
            )}
          </div>
        </div>
      )}

      <div>
        <label htmlFor={`${idPrefix}-clause`} className="label">
          Contract clause
        </label>
        <input
          id={`${idPrefix}-clause`}
          className="input"
          value={value.contractClause}
          onChange={patch("contractClause")}
          disabled={disabled}
          maxLength={200}
          placeholder="QITC Schedule 5 cl. 7.1"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-start`} className="label">
            Start date <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-start`}
            type="date"
            className="input"
            value={value.startDate}
            onChange={patch("startDate")}
            disabled={disabled}
            aria-invalid={!!errors.startDate}
          />
          {errors.startDate && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.startDate}
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-expiry`} className="label">
            Expiry date <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-expiry`}
            type="date"
            className="input"
            value={value.expiryDate}
            onChange={patch("expiryDate")}
            disabled={disabled}
            aria-invalid={!!errors.expiryDate}
          />
          {errors.expiryDate && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.expiryDate}
            </p>
          )}
          {coverageNote && (
            <p className="mt-1 text-caption text-amber-600 dark:text-amber-400">
              {coverageNote}
            </p>
          )}
        </div>
      </div>

      <fieldset>
        <legend className="label">Reminder days before expiry</legend>
        <div className="flex flex-wrap gap-3">
          {REMINDER_OPTIONS.map((day) => (
            <label
              key={day}
              className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300"
            >
              <input
                type="checkbox"
                className="accent-brand-600"
                checked={value.reminderDays.includes(day)}
                onChange={() => toggleReminder(day)}
                disabled={disabled}
              />
              {day}d
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <EvidenceLinksField
          value={value.evidenceLinks}
          onChange={(v) => onChange({ evidenceLinks: v })}
          disabled={disabled}
          firstLinkLabel={DEFAULT_EVIDENCE_LABEL}
        />
        {errors.evidenceLinks && (
          <p className="mt-1 text-caption text-red-600 dark:text-red-400">
            {errors.evidenceLinks}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-notes`} className="label">
          Notes
        </label>
        <textarea
          id={`${idPrefix}-notes`}
          className="input min-h-[80px]"
          value={value.notes}
          onChange={patch("notes")}
          disabled={disabled}
          maxLength={2000}
        />
      </div>
    </div>
  );
}
